import React from "react";
import moment from "moment";
import _ from "underscore";
import lodash from "lodash";
import { nanoid } from "nanoid";
import ClientData, {
  deleteGoal,
  deleteCardioGoal,
  activateNutritionGoal,
  getCurrentWorkout, // Add this import
  saveHealthReportClientData, // Add this import
} from "../../../../../data/ClientData";
import { sendMessageToUsersGroup } from "../../../../../data/MessageData";
import useRecipes from "../../../../../hooks/useRecipes";
import useProtocolHistory from "../../../../../hooks/useProtocolHistory";
import useClientExerciseReports from "../../../../../hooks/useClientExerciseReports";
import useClientNutritionChartData from "../../../../../hooks/useClientNutritionChartData";
import useEvents from "../../../../../hooks/useEvents";

export const useClientData = (uid, organizationId) => {
  const isValid = Boolean(uid && organizationId);

  // State
  const [state, setState] = React.useState({
    protocol: undefined,
    activeProtocol: undefined,
    coaches: undefined,
    selectedDay: new Date(),
    coachesUpdated: undefined,
    dailyReport: null,
    currentWorkout: null, // Add this for tracking current workout
    calendarModifiers: {},
    modifierStyles: {},
    client: null,
    photoUpdate: new Date(),
    messageGroup: null,
    mealPlanSelectOpen: false,
    coachSelectOpen: false,
    clientEditOpen: false,
    bodyMeasurementOpen: false,
    macroSelectOpen: false,
    cardioSelectOpen: false,
    nutritionGoalsDialogOpen: false,
    cardioGoalsDialogOpen: false,
    selectedNutritionGoal: null,
    selectedCardioGoal: null,
    chartType: undefined,
    historyOpen: false,
    protocolHistoryOpen: false,
    mode: "client",
    activeTab: 0,
  });

  // Refs
  const selectedProtocol = React.useRef();

  // Hooks
  const events = useEvents();
  const { mealPlans, getMealPlanById } = useRecipes({ organizationId });
  const { protocolHistory } = useProtocolHistory({ uid, organizationId });



  const monthStamp = moment().format("YYYY-MM");
  const currentMonthRange = React.useMemo(() => {
    const m = moment(monthStamp, "YYYY-MM");
    return {
      start: m.clone().startOf("month").toDate(),
      end: m.clone().endOf("month").toDate(),
    };
  }, [monthStamp]);

  const { exerciseData } = useClientExerciseReports({
    uid: uid,
    organizationId: organizationId,
    start: currentMonthRange.start,
    end: currentMonthRange.end,
  });

  const { nutritionChartData } = useClientNutritionChartData({
    uid: uid,
    organizationId: organizationId,
    start: currentMonthRange.start,
    end: currentMonthRange.end,
  });

  // Add effect to fetch current workout
  React.useEffect(() => {
    const fetchCurrentWorkout = async () => {
      // Only fetch if both uid and organizationId are valid
      if (isValid) {
        try {
          const currentWorkout = await getCurrentWorkout(uid);
          setState((prev) => ({ ...prev, currentWorkout }));
        } catch (error) {
          console.error("Error fetching current workout:", error);
        }
      }
    };

    fetchCurrentWorkout();
  }, [uid, organizationId, isValid]);

  // Memoized values
  const coachSummary = React.useMemo(() => {
    let summary = "";
    if (state.coaches) {
      const filteredCoaches = state.coaches.filter((coach) => coach !== null);
      filteredCoaches.forEach((coach) => {
        summary += `${coach?.firstName} ${coach?.lastName}, `;
      });
      summary = summary.slice(0, -2);
    }
    return summary;
  }, [state.coaches]);

  const coachesArray = React.useMemo(() => {
    if (!state.coaches) return null;

    const filteredCoaches = state.coaches.filter((coach) => coach !== null);
    return filteredCoaches.map((coach) => ({
      key: coach.uid,
      url: coach.avatarUrl,
      firstName: coach.firstName,
      lastName: coach.lastName,
    }));
  }, [state.coaches]);

  // Handler functions
  const handleButtonEvent = (ButtonName, PageName, Tab) => {
    const logEvent = { buttonName: ButtonName, pageName: PageName, tab: Tab };
    events.logButtonClick(logEvent);
  };

  // Nutrition goal handlers
  const handleNutritionGoalEdit = async (nutritionGoal) => {
    // Initialize newGoals as an empty array if nutritionGoals doesn't exist
    const newGoals = state.client.nutritionGoals
      ? [...state.client.nutritionGoals]
      : [];

    handleButtonEvent("Edit Nutrition Goal", "Clients", state.client.uid);
    const index = _.findIndex(newGoals, { id: nutritionGoal.id });
    const hadAnyActive = newGoals.some((g) => g.active);

    let changeMessage = "";
    let persistedGoal = nutritionGoal;

    if (index !== -1) {
      // Editing an existing goal: if nothing was active, make this one active so UI/reports resolve an active goal
      if (!hadAnyActive && !nutritionGoal.active) {
        persistedGoal = { ...nutritionGoal, active: true };
      }
      newGoals[index] = persistedGoal;
      changeMessage = `Your coach changed the "${persistedGoal.name}" nutrition goal. Proteins: ${
        persistedGoal?.proteins ? persistedGoal.proteins.toFixed(0) : "0"
      }g, carbs: ${
        persistedGoal?.carbs ? persistedGoal.carbs.toFixed(0) : "0"
      }g, and fats: ${
        persistedGoal?.fats ? persistedGoal.fats.toFixed(0) : "0"
      }g.`;
    } else {
      handleButtonEvent("Add Nutrition Goal", "Clients", state.client.uid);
      // First goal (or no active goal yet): must be active — UI uses find(g => g.active), same as first cardio goal
      const shouldActivate =
        newGoals.length === 0 || !newGoals.some((g) => g.active);
      persistedGoal = shouldActivate
        ? { ...nutritionGoal, active: true }
        : nutritionGoal;
      newGoals.push(persistedGoal);
      changeMessage = `Your coach added the "${persistedGoal.name}" nutrition goal. Proteins: ${
        persistedGoal?.proteins ? persistedGoal.proteins.toFixed(0) : "0"
      }g, carbs: ${
        persistedGoal?.carbs ? persistedGoal.carbs.toFixed(0) : "0"
      }g, and fats: ${
        persistedGoal?.fats ? persistedGoal.fats.toFixed(0) : "0"
      }g.`;
    }

    const result = await ClientData.updateClient(state.client.uid, {
      nutritionGoals: newGoals,
      nutritionGoalChanged: new Date(),
    });

    if (result && persistedGoal.active) {
      await saveHealthReportClientData({
        uid: state.client.uid,
        date: moment().toDate(),
        data: {
          proteins: persistedGoal.proteins,
          carbs: persistedGoal.carbs,
          fats: persistedGoal.fats,
          water: persistedGoal.water,
          fiber: persistedGoal.fiber || 0,
          activeGoalName: persistedGoal.name,
          activeGoalId: persistedGoal.id,
        },
      });
    }

    if (result) {
      await sendMessageToUsersGroup({
        mustBeRegistered: true,
        uid: state.client.uid,
        message: changeMessage,
      });
    }
  };

  const handleNutritionGoalDelete = async (nutritionGoal) => {
    handleButtonEvent("Delete Nutrition Goal", "Clients", state.client.uid);
    await deleteGoal({ nutritionGoal, client: state.client });
  };

  const handleNutritionGoalActivate = async (nutritionGoal) => {
    handleButtonEvent("Set Active Nutrition Goal", "Clients", state.client.uid);
    await activateNutritionGoal(state.client, nutritionGoal);
    await sendMessageToUsersGroup({
      uid: state.client.uid,
      message: `Your coach changed your active nutrition goal to "${nutritionGoal.name}".`,
    });
  };

  // Cardio goal handlers
  const handleCardioGoalEdit = async (cardioGoal) => {
    let goals = [];
    if (state.client.cardioGoals) {
      goals = state.client.cardioGoals;
    }

    if (goals) {
      handleButtonEvent("Edit Cardio Goal", "Clients", state.client.uid);
      const newGoals = [].concat(goals);
      const index = _.findIndex(newGoals, { id: cardioGoal.id });

      let changeMessage = "";
      if (index !== -1) {
        newGoals[index] = cardioGoal;
        changeMessage = `Your coach changed the ${cardioGoal.name} cardio goal.`;
      } else {
        handleButtonEvent("Add Cardio Goal", "Clients", state.client.uid);
        if (newGoals.length === 0) {
          cardioGoal.active = true;
        }
        newGoals.push(cardioGoal);
        changeMessage = `Your coach added the ${cardioGoal.name} cardio goal.`;
      }

      const result = await ClientData.updateClient(state.client.uid, {
        cardioGoals: newGoals,
        cardioGoalChanged: new Date(),
      });

      // If this is an active cardio goal, update the health report data
      if (cardioGoal.active) {
        await saveHealthReportClientData({
          uid: state.client.uid,
          date: moment().toDate(),
          data: {
            cardioGoals: [cardioGoal],
          },
        });
      }

      if (result) {
        await sendMessageToUsersGroup({
          mustBeRegistered: true,
          uid: state.client.uid,
          message: changeMessage,
        });
      }
    }
  };

  const handleCardioGoalDelete = async (cardioGoal) => {
    handleButtonEvent("Delete Cardio Goal", "Clients", state.client.uid);
    await deleteCardioGoal({ cardioGoal, client: state.client });
  };

  const handleCardioGoalActivate = async (cardioGoal) => {
    if (state.client.cardioGoals) {
      const goals = lodash.cloneDeep(state.client.cardioGoals);
      goals.forEach((goal) => {
        goal.active = false;
      });

      goals.forEach((goal) => {
        if (goal.id === cardioGoal.id) {
          goal.active = true;
        }
      });

      handleButtonEvent("Set Active Cardio Goal", "Clients", state.client.uid);

      await ClientData.updateClient(state.client.uid, {
        cardioGoals: goals,
        cardioGoalChanged: new Date(),
      });

      // Update the health report with the active cardio goal
      await saveHealthReportClientData({
        uid: state.client.uid,
        date: moment().toDate(),
        data: {
          cardioGoals: [cardioGoal],
        },
      });

      await sendMessageToUsersGroup({
        uid: state.client.uid,
        message: `Your coach changed your active cardio goal to "${cardioGoal.name}".`,
      });
    }
  };

  // Return a complete object with placeholder values when inputs are invalid
  // But always return the same shape of object to maintain hook consistency
  return {
    client: isValid ? state.client : null,
    protocol: isValid ? state.protocol : null,
    activeProtocol: isValid ? state.activeProtocol : null,
    coaches: isValid ? state.coaches : null,
    dailyReport: isValid ? state.dailyReport : null,
    currentWorkout: isValid ? state.currentWorkout : null,
    messageGroup: isValid ? state.messageGroup : null,
    exerciseData: isValid ? exerciseData : null,
    nutritionChartData: isValid ? nutritionChartData : null,
    calendarModifiers: isValid ? state.calendarModifiers : {},
    modifierStyles: isValid ? state.modifierStyles : {},
    coachSummary: isValid ? coachSummary : "",
    coachesArray: isValid ? coachesArray : null,
    mealPlans: isValid ? mealPlans : [],
    getMealPlanById: isValid ? getMealPlanById : () => null,
    protocolHistory: isValid ? protocolHistory : [],
    selectedProtocol: isValid ? selectedProtocol : { current: null },
    state,
    setState,
    handlers: {
      handleButtonEvent: isValid ? handleButtonEvent : () => {},
      handleNutritionGoalEdit: isValid ? handleNutritionGoalEdit : () => {},
      handleNutritionGoalDelete: isValid ? handleNutritionGoalDelete : () => {},
      handleNutritionGoalActivate: isValid
        ? handleNutritionGoalActivate
        : () => {},
      handleCardioGoalEdit: isValid ? handleCardioGoalEdit : () => {},
      handleCardioGoalDelete: isValid ? handleCardioGoalDelete : () => {},
      handleCardioGoalActivate: isValid ? handleCardioGoalActivate : () => {},
      mealPlans: isValid ? mealPlans : [],
      getMealPlanById: isValid ? getMealPlanById : () => null,
      coachesArray: isValid ? coachesArray : [],
      openMacroDialog: isValid
        ? (nutritionGoal = null) => {
            setState((prev) => ({
              ...prev,
              macroSelectOpen: true,
              selectedNutritionGoal: nutritionGoal || {
                id: nanoid(),
                name: "",
                proteins: 0,
                carbs: 0,
                fats: 0,
                water: 0,
                fiber: 0,
                active: false,
              },
            }));
          }
        : () => {},
      openNutritionGoalsDialog: isValid
        ? () => {
            handleButtonEvent(
              "View Nutrition Goals",
              "Clients",
              state.client?.uid,
            );
            setState((prev) => ({ ...prev, nutritionGoalsDialogOpen: true }));
          }
        : () => {},
      openCardioGoalsDialog: isValid
        ? () => {
            handleButtonEvent(
              "View Cardio Goals",
              "Clients",
              state.client?.uid,
            );
            setState((prev) => ({ ...prev, cardioGoalsDialogOpen: true }));
          }
        : () => {},
      openCardioDialog: isValid
        ? (cardioGoal = null) => {
            setState((prev) => ({
              ...prev,
              cardioSelectOpen: true,
              selectedCardioGoal: cardioGoal || {
                id: nanoid(),
                name: "New Cardio Goal",
                sessions: 3,
                targetHR: "120-140",
                active: false,
              },
            }));
          }
        : () => {},
    },
  };
};
